import { useQuery } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { z } from "zod";

import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { api } from "~/shared/services/api/fetch-client.ts";
import { selectedCustomerAtom } from "~/shared/state/user-profile-state.ts";

// Zod schema for broker extension data validation
const BrokerSchema = z.object({
	// Aligns with the broker email contract in customer-schema.ts
	// (`z.string().email().nullable()`) so a malformed value can't flow into a
	// rendered mailto link. `.optional()` keeps it backward compatible for
	// existing API responses that omit the field on the screens sharing this
	// hook (TRAX page header, FTL quote flow).
	email: z.string().email().nullable().optional(),
	extension: z.string().nullable().optional(),
	name: z.string().nullable(),
});

export type Broker = z.infer<typeof BrokerSchema>;

export type BrokerMode = "LTL" | "FTL";

// Query key factory for broker extension
const brokerQueryKey = (
	customerId: number | null,
	mode: BrokerMode,
	brokerId: number | null,
) => ["broker-extension", customerId, mode, brokerId];

/**
 * Fetches broker extension from the API
 * Customer ID is automatically included in the request header
 */
async function fetchBroker(
	mode: BrokerMode,
	brokerId: number | null,
): Promise<Broker> {
	const searchParams: Record<string, string | number> = {
		mode,
		...(brokerId === null ? {} : { brokerId }),
	};

	const response = await api.get(apiRoutes.GETBROKER, {
		searchParams,
	});
	if (!response.ok) {
		throw new Error(`Failed to fetch broker extension: ${response.statusText}`);
	}
	const data = await response.json();

	// Validate the API response using Zod schema
	return BrokerSchema.parse(data);
}

/**
 * Hook to fetch and cache broker extension data for the selected customer
 *
 * @returns Query result with broker extension data, loading state, and error state
 */
export function useBroker(mode: BrokerMode) {
	const selectedCustomer = useAtomValue(selectedCustomerAtom);
	const customerId = selectedCustomer?.id ?? null;
	const brokerId = selectedCustomer?.primaryBroker?.id ?? null;

	const { data, error, isLoading } = useQuery({
		queryKey: brokerQueryKey(customerId, mode, brokerId),
		queryFn: () => fetchBroker(mode, brokerId),
		enabled: !!customerId && !!brokerId, // Need both a customer and broker id
		staleTime: 5 * 60 * 1000, // 5 minutes
		gcTime: 10 * 60 * 1000, // 10 minutes
		retry: 2,
	});

	return {
		broker: data,
		isLoading,
		error,
	};
}
